import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isAfter, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import toast from "react-hot-toast";

import { AuthContext } from "../../../../../../../shared/context/auth-context";
import { ClinicaContext } from "../../../../../../../shared/context/clinica-context";
import { useHttpClient } from "../../../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../../../shared/context/LoadingContext";

/**
 * O período do extrato do cliente.
 *
 * ─── PORQUE HÁ UM PASSO ANTES DO PDF ────────────────────────────────────────
 *
 * O endpoint exige data inicial e final — é a mesma rota que serve o relatório
 * de extrato, e aí quem o tira escolhe sempre o período. Daqui, de dentro da
 * ficha, a pergunta habitual é outra: «o que é que este cliente deve e já
 * pagou». Por isso o período vem preenchido de ponta a ponta do histórico e o
 * botão está a um clique — quem quiser um mês aperta, quem não quiser não
 * pensa no assunto.
 *
 * ─── O HISTÓRICO COMEÇA NO REGISTO DO CLIENTE ───────────────────────────────
 *
 * E não numa data fixa inventada: um cliente registado em 2019 tem movimentos
 * de 2019, e começar em «há 12 meses» escondia-os sem avisar. Quando o cliente
 * não traz data de registo — os importados do papel não trazem —, recua-se a
 * 2015, que é antes de haver sistema.
 *
 * ─── AS DATAS SÃO `DesktopDatePicker`, NÃO `type="date"` ────────────────────
 *
 * Eram dois `<input type="date">`. Um campo de data nativo é desenhado pelo
 * BROWSER, no idioma do browser — num Chrome em inglês aparecia «06/29/2026»,
 * e nenhum atributo nem CSS o corrige, porque o campo nem faz parte da página.
 * Visto na Prodente a 2026-10-26.
 *
 * O seletor do MUI desenha o campo ele próprio, e com o `ptBR` fica dd/MM/yyyy
 * em qualquer máquina — é o que o resto da aplicação já usa (a lista da
 * secretária, as despesas, os relatórios).
 *
 * Por causa disso o estado passou a guardar `Date`, e não texto: é o que o
 * seletor dá e recebe. O pedido ao servidor continua a sair em `yyyy-MM-dd`.
 */
const INICIO_SEM_DATA = new Date(2015, 0, 1);

function ModalExtrato({ aberto, fechar, clienteId, clienteData, showExtrato }) {
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();

  const [aEnviar, setAEnviar] = useState(false);
  const [datas, setDatas] = useState({ inicial: null, final: null });

  const historicoCompleto = () => ({
    inicial: clienteData?.created_at
      ? new Date(clienteData.created_at)
      : INICIO_SEM_DATA,
    final: new Date(),
  });

  /* Reabrir não deve trazer o período que se escolheu da vez anterior. */
  useEffect(() => {
    if (aberto) setDatas(historicoCompleto());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, clienteData?.created_at]);

  const mudar = (campo) => (valor) =>
    setDatas((prev) => ({ ...prev, [campo]: valor }));

  const gerar = async () => {
    /* `isValid` e não só «está preenchido»: quem escreve a data à mão passa
       por estados impossíveis a meio («32/1…»), e o seletor devolve-os como
       data inválida. Sem isto, o `format` abaixo rebentava. */
    if (!isValid(datas.inicial) || !isValid(datas.final)) {
      toast.error("Preencha as duas datas.");
      return;
    }
    if (isAfter(datas.inicial, datas.final)) {
      toast.error("A data inicial não pode ser depois da final.");
      return;
    }

    setAEnviar(true);
    startLoading();
    try {
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/entradas/getExtratoCliente/${clienteId}`,
        "POST",
        JSON.stringify({
          /* O servidor continua a receber o mesmo que recebia — o que mudou
             foi só o que o ecrã mostra e guarda. */
          startDate: format(datas.inicial, "yyyy-MM-dd"),
          endDate: format(datas.final, "yyyy-MM-dd"),
          clinicaId: clinica.clinica._id,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      if (!resposta?.movimentos?.length) {
        /* Um PDF em branco faz a pessoa pensar que o extrato falhou. Dizer que
           não há movimentos no período é a mesma informação, mais depressa. */
        toast("Sem movimentos neste período.");
        return;
      }

      showExtrato(resposta);
      fechar();
    } catch (err) {
      toast.error(err.message || "Não foi possível gerar o extrato.");
    } finally {
      setAEnviar(false);
      stopLoading();
    }
  };

  const periodoInteiro = () => setDatas(historicoCompleto());

  return (
    <Dialog open={aberto} onClose={fechar} maxWidth="xs" fullWidth>
      <DialogTitle>Extrato do cliente</DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {clienteData?.nome}
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <Stack direction="row" spacing={2}>
            <DesktopDatePicker
              label="De"
              inputFormat="dd/MM/yyyy"
              value={datas.inicial}
              onChange={mudar("inicial")}
              renderInput={(params) => (
                <TextField {...params} size="small" fullWidth />
              )}
            />
            <DesktopDatePicker
              label="Até"
              inputFormat="dd/MM/yyyy"
              value={datas.final}
              onChange={mudar("final")}
              renderInput={(params) => (
                <TextField {...params} size="small" fullWidth />
              )}
            />
          </Stack>
        </LocalizationProvider>

        <Box sx={{ mt: 1.5 }}>
          <Button size="small" onClick={periodoInteiro}>
            Todo o histórico
          </Button>
        </Box>

        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: "block", mt: 2 }}
        >
          As parcelas pagas entram pela data de pagamento; as pendentes, pela
          data de vencimento.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={fechar} disabled={aEnviar}>
          Cancelar
        </Button>
        <Button onClick={gerar} variant="contained" disabled={aEnviar}>
          {aEnviar ? "A gerar…" : "Gerar extrato"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ModalExtrato;
